/**
 * LOCANE motion layer — GSAP + ScrollTrigger + SplitText.
 * Two presets only, per LOCANE_Tech_Strategy.md priority list: motion is last,
 * not first. Kept deliberately minimal — no scroll-jacking, no parallax.
 *
 * 1. Fade-up section reveal: applied by listing the section's Liquid `section.id`
 *    below, never by touching the section's own .liquid file.
 * 2. Headline stagger: applied to specific headline elements, targeted by
 *    walking down from a section's Liquid `section.id` (see HEADLINE_SECTION_IDS
 *    below). Not a class on the element itself — Shopify's richtext setting
 *    sanitizer strips `class`/`id` attributes from user-authored HTML, so the
 *    element has to be found structurally instead.
 * 3. INCI signature reveal: the one bespoke interaction for this project (see
 *    sections/locane-inci-reveal.liquid — plain markup, not a richtext field,
 *    so classes/data-attributes are ours to use freely here). Pins the row
 *    list while scrubbing through it with scroll, each row lighting up in
 *    turn; the highlighted rows (the real INCI positions) end at full opacity
 *    and weight.
 */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (typeof gsap === 'undefined') return;

  gsap.registerPlugin(ScrollTrigger, SplitText);

  /**
   * Horizon's "squeeze" desktop layout (>=990px, see assets/scroll-container.js)
   * scrolls .page-wrapper internally instead of the window — html/body are
   * overflow: hidden there. ScrollTrigger defaults to watching the window, so
   * without this it silently never sees real scroll position on desktop/tablet
   * and every scroll-triggered animation fires instantly on load instead of on
   * scroll. Below 990px Horizon uses normal window scrolling, so ScrollTrigger's
   * default (undefined = window) is correct there. matchMedia re-evaluates and
   * rebuilds the triggers automatically if the viewport crosses 990px (e.g.
   * rotating an iPad), which a one-time check at load wouldn't handle.
   */
  ScrollTrigger.matchMedia({
    '(min-width: 990px)': function () {
      return buildScrollTriggers('.page-wrapper');
    },
    '(max-width: 989.98px)': function () {
      return buildScrollTriggers(window);
    },
  });

  function buildScrollTriggers(scroller) {
    // Section IDs (from templates/*.json) that get the fade-up reveal.
    var FADE_UP_SECTION_IDS = ['featured_product_mTUbKC'];

    FADE_UP_SECTION_IDS.forEach(function (sectionId) {
      var el = document.querySelector('[id$="__' + sectionId + '"]');
      if (!el) return;
      gsap.from(el, {
        opacity: 0,
        y: 12,
        duration: 0.35,
        ease: 'power1.out',
        scrollTrigger: {
          trigger: el,
          scroller: scroller,
          start: 'top 90%',
          toggleActions: 'play none none reverse',
        },
      });
    });

    buildInciReveal(scroller);
  }

  // Section IDs whose first <h1>/<h2> gets the character-stagger reveal.
  var HEADLINE_SECTION_IDS = ['hero_iyt6zg'];

  HEADLINE_SECTION_IDS.forEach(function (sectionId) {
    var section = document.querySelector('[id$="__' + sectionId + '"]');
    var headline = section && section.querySelector('h1, h2');
    if (!headline) return;
    /*
      type: 'chars' alone wraps every character in its own inline-block div,
      which strips the browser's notion of "word" from the layout entirely —
      confirmed live: it broke "Serum" mid-word ("Peptide S" / "erum 01") on
      the Hero title once split, independent of overflow-wrap/word-break CSS
      (those only govern text-node wrapping, not a sequence of block-level
      char divs). Also splitting into 'words' first wraps each word in its
      own container div, so the browser wraps at the word-div boundary like
      normal text — chars still split (and animate) inside each word.
    */
    var split = new SplitText(headline, { type: 'words, chars' });
    gsap.from(split.chars, {
      opacity: 0,
      y: 20,
      rotateX: -40,
      duration: 0.6,
      stagger: 0.015,
      ease: 'expo.out',
    });
  });

  function buildInciReveal(scroller) {
    var inciSection = document.querySelector('[id$="__locane_inci_reveal"]');
    if (!inciSection || inciSection.querySelector('locane-formula')) return;
    var pin = inciSection.querySelector('.locane-inci__pin');
    var rows = inciSection.querySelectorAll('.locane-inci__row');
    if (!pin || !rows.length) return;

    // Starting offset for the x-translate below, so each row visibly moves
    // (not just changes color/opacity) as its turn in the scrub comes up.
    gsap.set(rows, { x: -8 });

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: pin,
        scroller: scroller,
        start: 'top top',
        // 100px of scroll per row (was 200) — 200 made the pin hold for far
        // longer than the visible change (opacity only) justified, reading as
        // the page being stuck.
        end: '+=' + rows.length * 100,
        pin: true,
        scrub: 0.5,
      },
    });
    rows.forEach(function (row) {
      var isHighlight = row.classList.contains('locane-inci__row--highlight');
      tl.to(row, {
        opacity: 1,
        x: 0,
        duration: 1,
      });
      if (isHighlight) {
        tl.to(row, { scale: 1.03, duration: 0.3, yoyo: true, repeat: 1 }, '<');
      }
    });
  }
})();
